import type {
  AddressOptionWithDays,
  DeliveryDayOption,
  SettingsConfirmationData,
} from '@library/api-customer-engagement-experience'

export type ResubscribeDraft = {
  address: AddressOptionWithDays
  day: DeliveryDayOption
  boxSize: { numPortions: number }
}

export type DraftAction =
  | { type: 'selectDay'; daySlotLeadTimeId: string }
  | { type: 'selectAddress'; address: AddressOptionWithDays }
  | { type: 'selectBoxSize'; boxSize: { numPortions: number } }

// `address.current` carries no delivery days of its own; they live on its entry in `address.options`.
const getCurrentAddress = (settings: SettingsConfirmationData): AddressOptionWithDays => {
  const currentAddress = settings.address.options.find(
    (option) => option.addressId === settings.address.current.addressId,
  )

  if (!currentAddress) {
    throw new Error('address.current is missing from address.options')
  }

  return currentAddress
}

export const getDraftInitialState = (settings: SettingsConfirmationData): ResubscribeDraft => ({
  address: getCurrentAddress(settings),
  day: settings.deliveryDay.current,
  boxSize: {
    numPortions: settings.boxSize.current.numPortions,
  },
})

// The API's `next_box_date_label` covers the default day only; any other day's label is formatted
// from its date.
const formatNextBoxDateLabel = (date: string): string => {
  const parsed = new Date(date)
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(
    parsed,
  )
  const month = new Intl.DateTimeFormat('en-GB', { month: 'long', timeZone: 'UTC' }).format(parsed)

  return `${weekday} ${parsed.getUTCDate()} ${month}`
}

export const getNextBoxDateLabel = (
  day: DeliveryDayOption,
  defaultDay: DeliveryDayOption,
  defaultDayLabel: string,
): string =>
  day.daySlotLeadTimeId === defaultDay.daySlotLeadTimeId
    ? defaultDayLabel
    : formatNextBoxDateLabel(day.date)

export const draftReducer = (state: ResubscribeDraft, action: DraftAction): ResubscribeDraft => {
  switch (action.type) {
    case 'selectDay': {
      const day = state.address.deliveryDayOptions.find(
        (option) => option.daySlotLeadTimeId === action.daySlotLeadTimeId,
      )

      return day ? { ...state, day } : state
    }
    case 'selectBoxSize':
      return { ...state, boxSize: action.boxSize }
    case 'selectAddress': {
      const { address } = action
      const draftedDay = address.deliveryDayOptions.find(
        (option) => option.daySlotLeadTimeId === state.day.daySlotLeadTimeId,
      )

      return { ...state, address, day: draftedDay ?? address.deliveryDayOptions[0] }
    }
    default:
      return state
  }
}
